import { OmniFilter } from '@components/OmniFilter/OmniFilter';
import { useLibraryManager } from '@stores/LibraryManager/LibraryManager';
import { omniFilterPreferences } from '@stores/OmniFilterPreferences';
import { trackingStore } from '@stores/Tracking';
import { observer } from 'mobx-react';
import { useCallback } from 'react';
import { debounce } from 'throttle-debounce';

/**
 * The libraries omnifilter's state and tracking. The query lives on `libraryManager._omniFilter`,
 * which drives `sortedAndFilteredLibraries`. Tokens filter libraries by name substring or exact
 * library ID (see `pages/Libraries/utils/librariesOmniFilter.ts`). Filtering is live.
 *
 * Shared by the Filters pill (grid view) and the table's search line, which stands in for the
 * pill while the table shows, so there is one search on screen rather than two.
 */
export function useLibrariesOmniFilter() {
  const libraryManager = useLibraryManager();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const trackFilterDebounced = useCallback(
    debounce(500, (query: string | null) => {
      trackingStore.libraryFilterApplied({ query, context: 'libraries' });
    }),
    [],
  );

  const onChange = useCallback(
    (next: string) => {
      libraryManager.setOmniFilter(next || null);
      trackFilterDebounced(next.trim() || null);
    },
    [libraryManager, trackFilterDebounced],
  );

  const onClear = useCallback(() => libraryManager.setOmniFilter(null), [libraryManager]);

  const onMatchAllChange = useCallback((matchAll: boolean) => {
    omniFilterPreferences.setMatchAll(matchAll);
    trackingStore.libraryFilterMatchModeChanged({ matchAll, context: 'libraries' });
  }, []);

  const onOpen = useCallback((via: 'keyboard' | 'click') => {
    trackingStore.libraryFilterOpened({ context: 'libraries', via });
  }, []);

  return {
    applied: libraryManager._omniFilter ?? '',
    onChange,
    onClear,
    matchAll: omniFilterPreferences.matchAll,
    onMatchAllChange,
    onOpen,
  };
}

/** The libraries omnifilter as a "Filters" pill: the reusable {@link OmniFilter}, bound to the manager. */
export const LibrariesOmniFilter = observer(({ compact = false }: { compact?: boolean }) => {
  const omniFilter = useLibrariesOmniFilter();
  return (
    <OmniFilter
      {...omniFilter}
      placeholder="Name or library ID"
      tooltip="Filter libraries"
      compact={compact}
    />
  );
});
